import { useState, useEffect, useRef } from "react";
import {
  useGetSpaceReceiptConfig,
  getGetSpaceReceiptConfigQueryKey,
  useUpdateSpaceReceiptConfig,
  useGetSpaceInvoiceTemplate,
  getGetSpaceInvoiceTemplateQueryKey,
  useUpdateSpaceInvoiceTemplate,
  useListSpaces,
} from "@workspace/api-client-react";
import type { ReceiptConfig, InvoiceTemplateConfig } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import { uploadLogo } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2 } from "lucide-react";
import { InvoiceHtmlPreview } from "@/components/billing/invoice-html-preview";
import { ReceiptHtmlPreview } from "@/components/settings/receipt-preview";
import { toast } from "sonner";

// Sample invoice data for the live invoice preview (no fetch needed).
const INVOICE_SAMPLE = {
  invoiceNumber: "03/062026",
  invoiceDate: "2026-06-15",
  dueDate: "2026-06-22",
  memberId: 42,
  memberName: "Acme Innovations Pvt. Ltd",
  memberContactName: "Rajesh Kumar",
  memberIsCompany: true,
  memberMobile: "98765 43210",
  memberGstin: "07AAACA1234A1Z5",
  memberAddress: "2nd Floor, Sample Tower, Connaught Place, New Delhi, 110001",
  memberState: "Delhi",
  issuerStateCode: "07",
  issuerGstin: "07AABCU9603R1ZX",
  subtotal: "5000.00",
  cgstTotal: "450.00",
  sgstTotal: "450.00",
  igstTotal: "0",
  total: "5900.00",
  amountPaid: "5900.00",
  documentType: "Invoice",
  lineItems: [
    {
      description: "Plan Rental — Workstation",
      hsnSacCode: "997212",
      quantity: 1,
      unitPrice: 5000,
      taxableAmount: 5000,
      gstRate: 18,
      lineTotal: 5900,
    },
  ],
};

interface FormState {
  // shared business identity (written to BOTH documents)
  logoUrl: string | null;
  businessName: string;
  gstin: string;
  address: string;
  // receipt-only
  upi: string;
  bankAccountName: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankName: string;
  bankBranch: string;
  orderReceiptStartNumber: string;
  terms: string[];
  receiptNotes: string;
  // invoice-only
  contactInfo: string;
  invoicePaymentMode: string;
  footer: string;
  invoiceNotes: string;
}

const EMPTY: FormState = {
  logoUrl: null,
  businessName: "",
  gstin: "",
  address: "",
  upi: "",
  bankAccountName: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
  bankBranch: "",
  orderReceiptStartNumber: "",
  terms: [],
  receiptNotes: "",
  contactInfo: "",
  invoicePaymentMode: "",
  footer: "",
  invoiceNotes: "",
};

export default function ReceiptInvoiceSettingsPage() {
  const { user } = useAuth();
  const { data: spaces = [] } = useListSpaces();
  const spaceId = user?.spaceId || spaces[0]?.id || 0;
  const space = spaces.find((s) => s.id === spaceId);

  const { data: receiptCfg } = useGetSpaceReceiptConfig(spaceId, {
    query: { enabled: spaceId > 0, queryKey: getGetSpaceReceiptConfigQueryKey(spaceId) },
  });
  const { data: invoiceCfg } = useGetSpaceInvoiceTemplate(spaceId, {
    query: { enabled: spaceId > 0, queryKey: getGetSpaceInvoiceTemplateQueryKey(spaceId) },
  });
  const updateReceipt = useUpdateSpaceReceiptConfig();
  const updateInvoice = useUpdateSpaceInvoiceTemplate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [snapshot, setSnapshot] = useState<FormState>(EMPTY);

  // Populate from the space + whichever configs are available. Tolerate the
  // receipt-config route being absent (older API) so the page still loads.
  useEffect(() => {
    if (!space || !invoiceCfg) return;
    const r = (receiptCfg ?? {}) as ReceiptConfig;
    const inv = invoiceCfg as InvoiceTemplateConfig;
    const next: FormState = {
      logoUrl: r.logoUrl ?? inv.logoUrl ?? null,
      businessName: r.issuerName ?? inv.issuerName ?? space.name ?? "",
      gstin: r.gstin ?? inv.gstin ?? space.gstin ?? "",
      address: r.address ?? inv.registeredAddress ?? space.registeredAddress ?? "",
      upi: r.upi ?? "",
      bankAccountName: r.bankAccountName ?? "",
      bankAccountNumber: r.bankAccountNumber ?? "",
      bankIfsc: r.bankIfsc ?? "",
      bankName: r.bankName ?? "",
      bankBranch: r.bankBranch ?? "",
      orderReceiptStartNumber:
        r.orderReceiptStartNumber != null ? String(r.orderReceiptStartNumber) : "",
      terms: r.terms ?? inv.terms ?? [],
      receiptNotes: r.notes ?? "",
      contactInfo: inv.contactInfo ?? "",
      invoicePaymentMode: inv.paymentMode ?? "",
      footer: inv.footer ?? "",
      invoiceNotes: inv.notes ?? "",
    };
    setForm(next);
    setSnapshot(next);
  }, [receiptCfg, invoiceCfg, space]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(snapshot);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }
  const onText =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      set(key, e.target.value as FormState[typeof key]);

  // Logo upload (shared)
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  async function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadLogo(file);
      if (res.ok) {
        set("logoUrl", res.url);
        toast.success("Logo uploaded.");
      } else {
        toast.error(res.message || "Upload failed.");
      }
    } finally {
      setUploading(false);
    }
  }

  // Terms helpers
  function setTerm(i: number, v: string) {
    setForm((p) => {
      const t = [...p.terms];
      t[i] = v;
      return { ...p, terms: t };
    });
  }
  const addTerm = () => setForm((p) => ({ ...p, terms: [...p.terms, ""] }));
  const removeTerm = (i: number) =>
    setForm((p) => ({ ...p, terms: p.terms.filter((_, j) => j !== i) }));

  async function handleSave() {
    const receiptPayload: ReceiptConfig = {
      issuerName: form.businessName || null,
      logoUrl: form.logoUrl || null,
      gstin: form.gstin || null,
      address: form.address || null,
      upi: form.upi || null,
      bankAccountName: form.bankAccountName || null,
      bankAccountNumber: form.bankAccountNumber || null,
      bankIfsc: form.bankIfsc || null,
      bankName: form.bankName || null,
      bankBranch: form.bankBranch || null,
      orderReceiptStartNumber: form.orderReceiptStartNumber.trim()
        ? Number(form.orderReceiptStartNumber)
        : null,
      terms: form.terms.map((t) => t.trim()).filter(Boolean),
      notes: form.receiptNotes || null,
    };
    const invoicePayload: InvoiceTemplateConfig = {
      logoUrl: form.logoUrl || null,
      issuerName: form.businessName || null,
      gstin: form.gstin || null,
      registeredAddress: form.address || null,
      contactInfo: form.contactInfo || null,
      paymentMode: form.invoicePaymentMode || null,
      terms: form.terms.map((t) => t.trim()).filter(Boolean),
      footer: form.footer || null,
      notes: form.invoiceNotes || null,
    };
    try {
      await Promise.all([
        updateReceipt.mutateAsync({ id: spaceId, data: receiptPayload }),
        updateInvoice.mutateAsync({ id: spaceId, data: invoicePayload }),
      ]);
      const saved = { ...form, terms: receiptPayload.terms ?? [] };
      setForm(saved);
      setSnapshot(saved);
      toast.success("Receipt & invoice settings saved.");
    } catch {
      toast.error("Failed to save. Try again.");
    }
  }

  const [tab, setTab] = useState<"receipt" | "invoice">("receipt");
  const saving = updateReceipt.isPending || updateInvoice.isPending;

  const invoiceOverride: InvoiceTemplateConfig = {
    logoUrl: form.logoUrl || null,
    issuerName: form.businessName || null,
    gstin: form.gstin || null,
    registeredAddress: form.address || null,
    contactInfo: form.contactInfo || null,
    paymentMode: form.invoicePaymentMode || null,
    terms: form.terms,
    footer: form.footer || null,
    notes: form.invoiceNotes || null,
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Receipt &amp; Invoice</h1>
        <p className="text-sm text-muted-foreground">
          Shared business details apply to both documents. The <b>Order Receipt</b> is sent
          before payment; the <b>Invoice</b> is shared after payment is received.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ── LEFT — all editable fields ── */}
        <div className="space-y-4">

      {/* Shared business details */}
      <Card>
        <CardHeader>
          <CardTitle>Business details (shared)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              {form.logoUrl ? (
                <img
                  src={form.logoUrl}
                  alt="Logo"
                  className="h-12 w-12 flex-shrink-0 rounded border bg-white object-contain"
                />
              ) : (
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded border bg-muted text-[10px] text-muted-foreground">
                  No logo
                </div>
              )}
              <Input
                type="url"
                className="flex-1"
                placeholder="Paste a URL, or upload →"
                value={form.logoUrl ?? ""}
                onChange={(e) => set("logoUrl", e.target.value || null)}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleLogoFile}
              />
              <Button
                type="button"
                variant="outline"
                className="flex-shrink-0"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>Business name</Label>
              <Input placeholder="Hoblix Space" value={form.businessName} onChange={onText("businessName")} />
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input placeholder="07AASFH9288D1ZR" value={form.gstin} onChange={onText("gstin")} />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea rows={2} value={form.address} onChange={onText("address")} />
          </div>
        </CardContent>
      </Card>

      {/* Commercial terms — shared, shown on BOTH the receipt and the invoice */}
      <Card>
        <CardHeader>
          <CardTitle>Commercial terms (receipt &amp; invoice)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.terms.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No custom terms — built-in defaults are used on the receipt. Add terms to show them
              on both documents.
            </p>
          )}
          {form.terms.map((t, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="mt-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background">
                {i + 1}
              </div>
              <Textarea
                rows={2}
                className="flex-1"
                value={t}
                onChange={(e) => setTerm(i, e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-1 flex-shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeTerm(i)}
                aria-label="Remove term"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addTerm}>
            <Plus className="mr-1 h-4 w-4" /> Add term
          </Button>
          <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-medium text-foreground">
              Dynamic fields (substituted on the document):
            </div>
            <code className="block text-[11px] leading-5">
              {"{{Due Date}} · {{Due Date + 7}} · {{Customer.Account Name}} · {{Customer.GSTNumber}} · {{Customer.Primary Name}} · {{Customer.Primary Phone Number}} · {{Customer Company Address}}"}
            </code>
            <div className="mt-1">
              Use these in terms or the note. Blank when the customer has no value (e.g. GSTIN, address).
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-document tabs: fields only (preview lives in the right column) */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as "receipt" | "invoice")}>
        <TabsList>
          <TabsTrigger value="receipt">Receipt</TabsTrigger>
          <TabsTrigger value="invoice">Invoice</TabsTrigger>
        </TabsList>

        {/* RECEIPT */}
        <TabsContent value="receipt" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Receipt numbering</CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label>Starting serial number</Label>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      placeholder="1"
                      className="max-w-[160px]"
                      value={form.orderReceiptStartNumber}
                      onChange={onText("orderReceiptStartNumber")}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Order receipts run as one continuous serial starting from this number — they never reset each month.
                      Leave blank to start at 1. The serial only ever moves forward, so set this before issuing your first receipt
                      (e.g. continue from your previous system&apos;s last number).
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Payment details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>UPI ID</Label>
                    <Input placeholder="pinelabs.stq4244191@pineaxis" value={form.upi} onChange={onText("upi")} />
                    <p className="mt-1 text-xs text-muted-foreground">
                      The receipt QR points to the current UPI ID; changing it needs an admin to
                      regenerate the QR image.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Account name</Label>
                      <Input value={form.bankAccountName} onChange={onText("bankAccountName")} />
                    </div>
                    <div>
                      <Label>Account number</Label>
                      <Input value={form.bankAccountNumber} onChange={onText("bankAccountNumber")} />
                    </div>
                    <div>
                      <Label>IFSC</Label>
                      <Input value={form.bankIfsc} onChange={onText("bankIfsc")} />
                    </div>
                    <div>
                      <Label>Bank name</Label>
                      <Input value={form.bankName} onChange={onText("bankName")} />
                    </div>
                    <div>
                      <Label>Branch</Label>
                      <Input value={form.bankBranch} onChange={onText("bankBranch")} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Note (optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    rows={2}
                    placeholder="Leave blank to auto-generate a note about the balance due."
                    value={form.receiptNotes}
                    onChange={onText("receiptNotes")}
                  />
                </CardContent>
              </Card>
        </TabsContent>

        {/* INVOICE */}
        <TabsContent value="invoice" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Invoice details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Payment mode</Label>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      value={form.invoicePaymentMode}
                      onChange={(e) => set("invoicePaymentMode", e.target.value)}
                    >
                      <option value="">—</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Card">Card</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Invoices are issued after payment, so they show the payment mode — no due date
                      or amount due.
                    </p>
                  </div>
                  <div>
                    <Label>Contact (phone / email / WhatsApp)</Label>
                    <Input placeholder="+91 98765 43210" value={form.contactInfo} onChange={onText("contactInfo")} />
                  </div>
                  <div>
                    <Label>Footer / Terms &amp; Conditions</Label>
                    <Textarea
                      rows={4}
                      placeholder="Payment is due within 7 days of invoice date. …"
                      value={form.footer}
                      onChange={onText("footer")}
                    />
                  </div>
                  <div>
                    <Label>Invoice notes (optional)</Label>
                    <Textarea
                      rows={2}
                      placeholder="Thank you for your business!"
                      value={form.invoiceNotes}
                      onChange={onText("invoiceNotes")}
                    />
                  </div>
                </CardContent>
              </Card>
        </TabsContent>
      </Tabs>

      {isDirty && (
        <Alert>
          <AlertDescription>You have unsaved changes.</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || !isDirty}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
        </div>

        {/* ── RIGHT — live preview carousel (Receipt ↔ Invoice) ── */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <Card className="flex flex-col overflow-hidden lg:h-[calc(100vh-7rem)]">
            <CardHeader className="pb-3">
              <div className="inline-flex self-start rounded-lg bg-muted p-0.5 text-sm">
                <button
                  type="button"
                  onClick={() => setTab("receipt")}
                  className={`rounded-md px-4 py-1.5 ${
                    tab === "receipt" ? "bg-background font-semibold shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  Receipt
                </button>
                <button
                  type="button"
                  onClick={() => setTab("invoice")}
                  className={`rounded-md px-4 py-1.5 ${
                    tab === "invoice" ? "bg-background font-semibold shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  Invoice
                </button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto bg-muted/30 p-0">
              {tab === "receipt" ? (
                <ReceiptHtmlPreview
                  config={{
                    logoUrl: form.logoUrl,
                    businessName: form.businessName,
                    gstin: form.gstin,
                    address: form.address,
                    upi: form.upi,
                    bankAccountName: form.bankAccountName,
                    bankAccountNumber: form.bankAccountNumber,
                    bankIfsc: form.bankIfsc,
                    bankName: form.bankName,
                    bankBranch: form.bankBranch,
                    terms: form.terms,
                    notes: form.receiptNotes,
                  }}
                />
              ) : (
                <InvoiceHtmlPreview
                  invoiceData={INVOICE_SAMPLE}
                  templateConfigOverride={invoiceOverride}
                  documentType="Invoice"
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
